import { Controller, useFormContext } from 'react-hook-form';
import { Field } from '@vnetwork/ui/forms';
import { Textarea, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import type { TicketFormValues } from '../ticket-form-schema';

/**
 * Content section — title/content + analysis fields.
 *
 * P1: plain textarea for ticket_content (no rich-text editor dependency).
 * Analysis fields (affected / avoid / root_cause / remedial_measures) are shown
 * for error_service group only, matching legacy section visibility.
 */
export function ContentSection() {
  const { control, formState: { errors }, watch } = useFormContext<TicketFormValues>();

  const typeGroup = watch('ticket_type_group');
  const isErrorService = typeGroup === 'error_service';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Content</CardTitle>
      </CardHeader>
      <CardBody>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-4)' }}>
        {/* Main content */}
        <Field
          label="Ticket Content"
          error={String(errors.ticket_content?.message ?? '') || undefined}
        >
          {(fp) => (
            <Controller
              name="ticket_content"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...fp}
                  {...field}
                  placeholder="Describe the issue or request in detail…"
                  invalid={!!errors.ticket_content}
                  style={{ minHeight: '120px', resize: 'vertical' }}
                />
              )}
            />
          )}
        </Field>

        {/* Error-service analysis fields */}
        {isErrorService && (
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Affected (Impact description)">
              {(fp) => (
                <Controller
                  name="ticket_affected"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...fp}
                      {...field}
                      placeholder="Who / what was affected?"
                      style={{ minHeight: '120px', resize: 'vertical' }}
                    />
                  )}
                />
              )}
            </Field>

            <Field label="Root Cause">
              {(fp) => (
                <Controller
                  name="ticket_root_cause"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...fp}
                      {...field}
                      placeholder="Root cause analysis"
                      style={{ minHeight: '120px', resize: 'vertical' }}
                    />
                  )}
                />
              )}
            </Field>

            <Field label="How to Avoid">
              {(fp) => (
                <Controller
                  name="ticket_avoid"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...fp}
                      {...field}
                      placeholder="Prevention / mitigation steps"
                      style={{ minHeight: '120px', resize: 'vertical' }}
                    />
                  )}
                />
              )}
            </Field>

            <Field label="Remedial Measures">
              {(fp) => (
                <Controller
                  name="ticket_remedial_measures"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...fp}
                      {...field}
                      placeholder="Steps taken to resolve"
                      style={{ minHeight: '120px', resize: 'vertical' }}
                    />
                  )}
                />
              )}
            </Field>
          </div>
        )}
      </div>
      </CardBody>
    </Card>
  );
}
